/**
 * Singleton accessor + typed RPC wrappers for the persistence worker.
 *
 * Every wrapper passes `[]` as the `transferables` argument. Without it,
 * `callWorker` defaults to `collectTransferables(msg)`, which walks the
 * payload and TRANSFERS every typed-array buffer to the worker — detaching
 * it on the main thread and corrupting the live store's geometry. The
 * empty array forces structured clone (a memcpy) instead, leaving the
 * source buffers intact.
 */

import {
  callWorker,
  createSingletonWorker,
} from "../../workers/createWorkerRPC.ts";
import type {
  DecodeDocumentRequest,
  DecodeDocumentResponse,
  EncodeDocumentRequest,
  EncodeDocumentResponse,
  LoadSessionRequest,
  LoadSessionResponse,
  SaveSceneCacheRequest,
  SaveSceneCacheResponse,
  SaveSessionRequest,
  SaveSessionResponse,
} from "./persistenceWorker.ts";
import type { RawSceneDocument, RawSessionDocument } from "./types.ts";

const getPersistenceWorker = createSingletonWorker(
  () =>
    new Worker(new URL("./persistenceWorker.ts", import.meta.url), {
      type: "module",
    }),
);

// Empty transferables — see file header. NEVER replace with `undefined`.
const NO_TRANSFER: Transferable[] = [];

/** Encode + stringify + write a scene to the IndexedDB cache. */
export function workerSaveSceneCache(
  key: string,
  raw: RawSceneDocument,
): Promise<SaveSceneCacheResponse> {
  return callWorker<SaveSceneCacheRequest, SaveSceneCacheResponse>(
    getPersistenceWorker(),
    { type: "save-scene-cache", key, raw },
    NO_TRANSFER,
    {
      stallWarningMs: 10_000,
      stallMessage: "Saving scene to cache is taking longer than expected.",
      stallDedupKey: "persistence-save-scene-cache",
    },
  );
}

/** Encode + stringify + write the session restore document. */
export function workerSaveSession(
  raw: RawSessionDocument,
): Promise<SaveSessionResponse> {
  return callWorker<SaveSessionRequest, SaveSessionResponse>(
    getPersistenceWorker(),
    { type: "save-session", raw },
    NO_TRANSFER,
    {
      stallWarningMs: 10_000,
      stallMessage: "Saving session is taking longer than expected.",
      stallDedupKey: "persistence-save-session",
    },
  );
}

/** Read + parse + decode the session restore document, or null if absent. */
export function workerLoadSession(): Promise<LoadSessionResponse> {
  return callWorker<LoadSessionRequest, LoadSessionResponse>(
    getPersistenceWorker(),
    { type: "load-session" },
    NO_TRANSFER,
  );
}

/** Encode + JSON.stringify a scene document for `.pr` file save. */
export function workerEncodeDocument(
  raw: RawSceneDocument,
): Promise<EncodeDocumentResponse> {
  return callWorker<EncodeDocumentRequest, EncodeDocumentResponse>(
    getPersistenceWorker(),
    { type: "encode-document", raw },
    NO_TRANSFER,
    {
      stallWarningMs: 10_000,
      stallMessage: "Scene serialization is taking longer than expected.",
      stallDedupKey: "persistence-encode-document",
    },
  );
}

/** JSON.parse + decode a `.pr` file body into a raw scene document. */
export function workerDecodeDocument(
  json: string,
): Promise<DecodeDocumentResponse> {
  return callWorker<DecodeDocumentRequest, DecodeDocumentResponse>(
    getPersistenceWorker(),
    { type: "decode-document", json },
    NO_TRANSFER,
    {
      stallWarningMs: 10_000,
      stallMessage: "Scene deserialization is taking longer than expected.",
      stallDedupKey: "persistence-decode-document",
    },
  );
}
